import {
  FluidButtonConfig,
  FluidButtonType,
  FluidComponentDefinition,
  FluidIconKey,
  FluidTheme,
} from '@lmig/fluid-core';
import { EventEmitter, FunctionalComponent, h } from '@stencil/core';

import { FluidComponentFromDefinition } from '../../../common/fluid-component-rendering.components';
import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../../utils/utils';

export type FluidTabsTranslationOptions = {
  [id: string]: {
    tabTitle?: string;
  };
};

export const TAB_BUTTON_ALLOWED_CHILDREN = [
  'fluid-icon',
  'fluid-text',
  'fluid-context-menu',
  'fluid-badge',
  'fluid-button',
];

// ================================================================ //
// -- Tab Icon

type TabIconProps = {
  tab: HTMLFluidTabElement;
  position: string;
  theme: FluidTheme;
};

const TabIcon = (props: TabIconProps): HTMLElement | null => {
  const { tab, position, theme } = props;
  const iconConfig = position === 'before' ? tab.iconBefore : tab.iconAfter;
  const iconSlot = position === 'before' ? tab._iconBefore : tab._iconAfter;

  if (!iconConfig && !iconSlot) return;

  const tabClass = {
    [`tab-icon-${position}`]: true,
    slotted: !!iconSlot,
  };

  return (
    <span innerHTML={iconSlot?.outerHTML} role='presentation' class={tabClass}>
      {!!iconConfig && (
        <fluid-icon
          config={{
            ...iconConfig,
            theme: iconConfig.theme ?? theme,
            overrideGlobalTheme: iconConfig.overrideGlobalTheme ?? true,
          }}
        />
      )}
    </span>
  );
};

// ================================================================ //
// -- Tab Button

type TabButtonProps = {
  tab: HTMLFluidTabElement;
  tabCanChange: boolean;
  setSelectedBtn: (tab: HTMLElement, btn: HTMLElement) => void;
  selectTab: (tab: HTMLFluidTabElement) => void;
  selectedTab: HTMLFluidTabElement;
  verticalLayout: boolean;
  theme: FluidTheme;
  i18nStore: FluidI18nStore;
  fluidTranslate: FluidTabsTranslationOptions;
};

const TabButton = (props: TabButtonProps) => {
  const {
    tab,
    tabCanChange,
    setSelectedBtn,
    selectTab,
    selectedTab,
    verticalLayout,
    theme,
    i18nStore,
    fluidTranslate,
  } = props;

  const contextContentDefinition: FluidComponentDefinition =
    Utils.validateComponentDefinition(
      tab.contextContentDefinition,
      TAB_BUTTON_ALLOWED_CHILDREN
    )
      ? { ...tab.contextContentDefinition }
      : null;

  const computeDisabled = () => {
    if (Utils.isEqual(selectedTab, tab)) return false;
    if (!tabCanChange) return true;

    const d = 'disabled';
    return tab.hasAttribute(d)
      ? tab.getAttribute(d) === 'true' || !tab.getAttribute(d)
      : false;
  };

  const computeTabIndex = () => {
    // https://www.w3.org/WAI/ARIA/apg/patterns/tabs/#keyboardinteraction
    // when tabIndex is equal to -1 Arrow key navigation is still available
    return Utils.isEqual(selectedTab, tab) ? 0 : -1;
  };
  const translatedTitle =
    i18nStore?.translate(fluidTranslate?.[tab.id]?.tabTitle) ||
    tab.tabTitle ||
    tab.title;

  return (
    !tab.hidden && (
      <button
        role='tab'
        aria-controls={tab.id}
        aria-selected={String(selectedTab === tab)}
        disabled={computeDisabled()}
        tabIndex={computeTabIndex()}
        onClick={() => selectTab(tab as HTMLFluidTabElement)}
        ref={(el) => setSelectedBtn(tab, el as HTMLElement)}
        class={{
          'fluid-tab-btn': true,
          [`${verticalLayout ? 'vertical' : 'horizontal'}`]: true,
          active: selectedTab === tab,
          disabled: computeDisabled(),
          'has-context-content': !!contextContentDefinition,
        }}>
        <TabIcon tab={tab} position='before' theme={theme} />
        <span class='tab-btn-label'>{translatedTitle}</span>
        {contextContentDefinition && (
          <div
            class={{
              'tab-context-content': true,
              'show-on-hover': contextContentDefinition.props?.showOnHover,
            }}>
            <FluidComponentFromDefinition
              definition={contextContentDefinition}
            />
          </div>
        )}
        <TabIcon tab={tab} position='after' theme={theme} />
      </button>
    )
  );
};

// ================================================================ //
// -- Tab Buttons Container

export type TabButtonsContainerProps = {
  tabs: HTMLFluidTabElement[];
  setSelectedBtn: (tab: HTMLElement, btn: HTMLElement) => void;
  setTabListRef: (el: HTMLElement) => void;
  selectTab: (tab: HTMLFluidTabElement) => void;
  selectedTab: HTMLFluidTabElement;
  tabCanChange: boolean;
  verticalLayout: boolean;
  showArrows: boolean;
  theme: FluidTheme;
  i18nStore: FluidI18nStore;
  fluidTranslate: FluidTabsTranslationOptions;
};

export const TabButtonsContainer = (props: TabButtonsContainerProps) => {
  const {
    tabs,
    setSelectedBtn,
    setTabListRef,
    selectTab,
    selectedTab,
    tabCanChange,
    verticalLayout,
    showArrows,
    theme,
    i18nStore,
    fluidTranslate,
  } = props;
  return (
    <div
      id='tabs-list'
      role='tablist'
      ref={(el) => setTabListRef(el as HTMLElement)}
      class={{
        'tabs-list': true,
        'nav-arrows': showArrows,
        'vertical-layout': verticalLayout,
      }}>
      {tabs.map((tab) => {
        const tabButtonProps: TabButtonProps = {
          tab,
          tabCanChange,
          setSelectedBtn,
          selectTab,
          selectedTab,
          verticalLayout,
          theme,
          i18nStore,
          fluidTranslate,
        };
        return <TabButton {...tabButtonProps} />;
      })}
    </div>
  );
};

// ================================================================ //
// -- "Add Tab" Button

export interface AddTabButtonProps extends FluidFunctionalComponentProps {
  config: FluidButtonConfig;
  verticalLayout: boolean;
  disabled: boolean;
  theme: FluidTheme;
  addTabButtonClicked: EventEmitter;
}

export const AddTabButton: FunctionalComponent<AddTabButtonProps> = (
  props: AddTabButtonProps
) => {
  const { config, verticalLayout, disabled, theme, addTabButtonClicked } =
    props;

  if (!config) return;
  return (
    <div
      class={{
        'add-tab-container': true,
        'vertical-layout': verticalLayout,
      }}>
      <fluid-button
        id='add-tab'
        class='add-tab-btn'
        onClick={() => addTabButtonClicked.emit()}
        config={{
          label: 'Add Tab',
          ...config,
          type: FluidButtonType.GHOST,
          removeMargin: true,
          iconBefore: { key: FluidIconKey.PLUS },
          overrideGlobalTheme: true,
          disabled,
          theme,
        }}
      />
    </div>
  );
};
